import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { delay, switchMap, take, tap } from "rxjs/operators";
import { ContentfulActionType } from "src/app/core/interfaces/contentful";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { DialogsService } from "src/app/core/services/dialogs.service";
import { closeLaunchpad, closeNews, openNews, openWebinars } from "src/app/ngrx/actions/overlay.actions";
import { navigateToSlot } from "src/app/ngrx/actions/router.actions";
import { openPanel } from "src/app/ngrx/actions/sidebar.actions";
import { SidebarService } from "src/app/shared/ui/sidebar/sidebar.service";
import { CommunalPlatformNewsEntry, TileOnTileEntry } from "./news.interfaces";

type PanelActions = Exclude<
  ContentfulActionType,
  | ContentfulActionType.NOTIFICATION_SETTINGS_DIALOG
  | ContentfulActionType.WEBINAR_DIALOG_REGULATED
  | ContentfulActionType.WEBINAR_DIALOG_SERVICES
  | ContentfulActionType.EXTERNAL_URL
  | ContentfulActionType.GLOSSARY_DIALOG
  | ContentfulActionType.NEWS_OVERLAY
  | ContentfulActionType.CONTACT_DIALOG
  | ContentfulActionType.CAMPUS_ONE_ORDER
>;

@Injectable({ providedIn: "root" })
export class NewsActionsManagerService {
  private readonly store = inject(Store);
  private readonly analytics = inject(AnalyticsService);
  private readonly sidebar = inject(SidebarService);
  private readonly dialogsService = inject(DialogsService);

  private readonly actionsPanels: Record<PanelActions, Array<string>> = {
    ARCHIVE_PANEL: ["archivePanel", "archive"],
    ENERGY_MONITOR_PANEL: ["energyMonitor", "monitor", "monitor_3yp", "outagesMonitor"],
    INSTALLATION_OVERVIEW_PANEL: ["installationOverview", "overview"],
    MAP_LAYERS_PANEL: ["mapLayers"],
    OBJECT_SELECTION_PANEL: ["objectSelection"],
    COCKPIT_PANEL: ["cockpit"],
    BENCHMARKING_PANEL: ["municipalityBenchmarking"],
    MEASURES_PANEL: ["measures"],
    MEASURES_LIST_PANEL: ["measuresListPanel"],
    CRISIS_MANAGEMENT_PANEL: ["crisisManagement"],
    DATA_STORAGE_PANEL: ["dataStorage"],
    POINTS_OF_CONTACT_PANEL: ["pointsOfContact"]
  };

  private readonly actionsDialogs = {
    NOTIFICATION_SETTINGS_DIALOG: "NOTIFICATION_SETTINGS",
    CONTACT_DIALOG: "CONTACT"
  };

  dispatch(action: ContentfulActionType, url?: string, entry?: CommunalPlatformNewsEntry | TileOnTileEntry): void {
    switch (action) {
      case ContentfulActionType.EXTERNAL_URL: {
        window.open(url, "_blank");
        break;
      }
      case ContentfulActionType.NEWS_OVERLAY: {
        this.store.dispatch(openNews());
        break;
      }
      case ContentfulActionType.NOTIFICATION_SETTINGS_DIALOG: {
        this.dialogsService.openNotificationSettingsDialog();
        break;
      }
      case ContentfulActionType.CONTACT_DIALOG: {
        this.dialogsService.openContactDialog();
        break;
      }
      case ContentfulActionType.WEBINAR_DIALOG_REGULATED:
      case ContentfulActionType.WEBINAR_DIALOG_SERVICES: {
        this.store.dispatch(closeNews());
        this.store.dispatch(openWebinars());
        break;
      }
      case ContentfulActionType.ARCHIVE_PANEL:
      case ContentfulActionType.ENERGY_MONITOR_PANEL:
      case ContentfulActionType.INSTALLATION_OVERVIEW_PANEL:
      case ContentfulActionType.MAP_LAYERS_PANEL:
      case ContentfulActionType.OBJECT_SELECTION_PANEL:
      case ContentfulActionType.COCKPIT_PANEL:
      case ContentfulActionType.MEASURES_PANEL:
      case ContentfulActionType.MEASURES_LIST_PANEL:
      case ContentfulActionType.CRISIS_MANAGEMENT_PANEL:
      case ContentfulActionType.DATA_STORAGE_PANEL:
      case ContentfulActionType.BENCHMARKING_PANEL: {
        if (!entry) {
          throw new Error("entry is required for this action");
        }
        // close all
        of(null)
          .pipe(
            tap(() => {
              this.store.dispatch(closeNews());
              this.store.dispatch(closeLaunchpad());
              this.store.dispatch(navigateToSlot({ slot: entry.moduleName }));
            }),
            delay(500),
            switchMap(() => this.sidebar.panels$),
            take(1)
          )
          .subscribe((panels) => {
            const panel = panels.find(({ name }) => this.actionsPanels[action].includes(name));

            if (panel) {
              this.store.dispatch(openPanel({ panelName: panel.name }));
            }
          });

        break;
      }
    }

    if (entry) {
      this.analytics.trackNewsActionClick(entry.moduleName, "headline" in entry ? entry.headline : action);
    }
  }
}
